import { DialogTitle } from "@mui/material";
import { dualLangInspectionNoteBusiness } from "api/bussiness/inspection-note/dual-lang-inspection-note/dual-lang-inspection-note-business";
import { productBussiness } from "api/bussiness/product/product-bussiness";
import { workOrderBusiness } from "api/bussiness/work-order/work-order-bussiness";
import { workOrderPoBusiness } from "api/bussiness/work-order-po/work-order-po-bussiness";
import {
  InspectionConclusionEnum,
  PrintColorInspect,
} from "commons/enum/inspection";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { DynamicFormArray } from "commons/ultilities/dynamic-form/components/dynamic-form-array";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import {
  IControl,
  IControlProperties,
} from "commons/ultilities/dynamic-form/models/control";
import { createProductOptionLabel } from "commons/ultilities/option-item/label-ultil";
import { cloneDeep } from "lodash";
import { observer } from "mobx-react-lite";
import { IUpdateDualLangInspectionNote } from "mobx-stores/_models/inspection-note/dual-lang-inspection-note/dual-lang-inspection-note";
import { IUpdateDualLangInspectionNoteItem } from "mobx-stores/_models/inspection-note/dual-lang-inspection-note/dual-lang-inspection-note-item";
import { IProductProperties } from "mobx-stores/_models/products/product";
import { IWorkOrderPoProperties } from "mobx-stores/_models/work-order-po/work-order-po";
import { IWorkOrderProperties } from "mobx-stores/_models/work-order/work-order";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useEffect, useMemo } from "react";
import { DialogComplexContent } from "share-components/templates/form/dialog-complex-form/dialog-complex-content";
import { DialogComplexFormContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form";
import { DialogComplexFormArrayContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form-array";
import { DialogForm } from "share-components/templates/form/dialog-form";
import { getEditDualLangInspectionNoteFormSchema } from "../schema";
import {
  apperanceInspectDefaultText,
  approvalInspectDefaultText,
  conclusionDefautText,
  createApprovalStandard,
  createHeightStandard,
  createLengthStandard,
  createPaperTypeStandard,
  createPrintColorStandard,
  createThicknessStandard,
  createWeightStandard,
  createWidthStandard,
  getOthersStandard,
  inspectionItemDefaultValues,
  otherInspectDefaultText,
} from "../share";

export const EditDualLangInspectionNote = observer(() => {
  const {
    dualLangInspectionNoteManagement: {
      openEdit,
      getDualLangInspectionNoteDetail,
      setProp,
      dualLangInspectionNoteDetail,
    },
    setProp: setRootStoreProp,
  } = useStores();
  const {
    form: { controls, isValid },
    getControl,
    getControlArray,
    setDefaults,
    submitHandler,
    getArrayItemControl,
  } = useForm<IUpdateDualLangInspectionNote>(
    (form) => getEditDualLangInspectionNoteFormSchema(form),
    {
      id: dualLangInspectionNoteDetail?.id,
      expireDateText: dualLangInspectionNoteDetail?.expireDateText || "",
      inspectionDate: dualLangInspectionNoteDetail?.inspectionDate || "",
      number: dualLangInspectionNoteDetail?.number || "",
      workOrderId: dualLangInspectionNoteDetail?.workOrderId || "",
      workOrderPOId: dualLangInspectionNoteDetail?.workOrderPOId || "",
      conclusion: conclusionDefautText,
      customerId: "",
      deliveredQuantity: dualLangInspectionNoteDetail?.deliveredQuantity || 0,
      inspectionSamples: dualLangInspectionNoteDetail?.inspectionSamples || "",
      inspector: dualLangInspectionNoteDetail?.inspector || "",
      note: dualLangInspectionNoteDetail?.note || "",
      packagesQuantity: dualLangInspectionNoteDetail?.packagesQuantity || "",
      purchaseOrderId: dualLangInspectionNoteDetail?.purchaseOrderId || "",
      quantityPerPackage: dualLangInspectionNoteDetail?.quantityPerPackage || 0,
      storageTemperature:
        dualLangInspectionNoteDetail?.storageTemperature || "",

      items: [],
    }
  );

  const itemControl = useMemo(() => {
    return getControl("items");
  }, [getControl]);

  const cleanItems = useCallback(() => {
    if (itemControl) {
      itemControl.setProp("children", []);
    }
  }, [itemControl]);

  const setOptionItemsForProductIsEmty = useCallback(() => {
    if (itemControl) {
      const newChildSchema = cloneDeep(itemControl.childSchema);
      const productIdSchema = newChildSchema.productId as IControlProperties;
      productIdSchema.optionItems = [];

      itemControl.setChildSchema(newChildSchema);
    }
  }, [itemControl]);

  const setOptionItemsForProduct = useCallback(
    (wo: IWorkOrderProperties) => {
      if (itemControl) {
        const newChildSchema = cloneDeep(itemControl.childSchema);
        const productIdSchema = newChildSchema.productId as IControlProperties;
        productIdSchema.optionItems = wo.items?.map((x) => ({
          label: createProductOptionLabel(x.product as IProductProperties),
          value: x.product?.id,
        }));

        itemControl.setChildSchema(newChildSchema);
      }
    },
    [itemControl]
  );

  const setOptionItemsForProductFromWorkOrderPo = useCallback(
    (wo: IWorkOrderPoProperties) => {
      if (itemControl) {
        const newChildSchema = cloneDeep(itemControl.childSchema);
        const productIdSchema = newChildSchema.productId as IControlProperties;
        const product = wo.purchaseOrderItem?.product;
        productIdSchema.optionItems = product
          ? [
              {
                label: createProductOptionLabel(product as IProductProperties),
                value: product.id,
              },
            ]
          : [];

        itemControl.setChildSchema(newChildSchema);
      }
    },
    [itemControl]
  );

  const setOptionItemsSchemaForProduct = useCallback(async () => {
    if (!dualLangInspectionNoteDetail) return;

    const hasWorkOrder = !!dualLangInspectionNoteDetail.workOrderId;
    const hasWorkOrderPO = !!dualLangInspectionNoteDetail.workOrderPOId;
    if (!hasWorkOrder && !hasWorkOrderPO) return;

    if (hasWorkOrderPO) {
      const woDetail = await workOrderPoBusiness.getById(
        dualLangInspectionNoteDetail.workOrderPOId!
      );
      setOptionItemsForProductFromWorkOrderPo(woDetail);

      if (
        dualLangInspectionNoteDetail.items &&
        dualLangInspectionNoteDetail.items.length > 0
      ) {
        const product = woDetail.purchaseOrderItem?.product;
        const productOption = product
          ? [
              {
                value: product.id,
                label: createProductOptionLabel(product as IProductProperties),
              },
            ]
          : [];
        getControl("items").children.forEach((row: IControl[]) => {
          const idCtr = row.filter((x) => x.name === "id")?.[0];
          const data = dualLangInspectionNoteDetail.items.filter(
            (x) => idCtr && x.id === idCtr.value
          )?.[0];
          const productIdCtr = row.filter((x) => x.name === "productId")?.[0];
          if (productIdCtr && data.product) {
            productIdCtr.setProp("optionItems", productOption);
          }
        });
      }
      return;
    }

    if (hasWorkOrder) {
      const woDetail = await workOrderBusiness.getById(
        dualLangInspectionNoteDetail.workOrderId!
      );
      setOptionItemsForProduct(woDetail);

      if (
        dualLangInspectionNoteDetail.items &&
        dualLangInspectionNoteDetail.items.length > 0
      ) {
        getControl("items").children.forEach((row: IControl[]) => {
          const idCtr = row.filter((x) => x.name === "id")?.[0];
          const data = dualLangInspectionNoteDetail.items.filter(
            (x) => idCtr && x.id === idCtr.value
          )?.[0];
          const productIdCtr = row.filter((x) => x.name === "productId")?.[0];
          if (productIdCtr && data.product) {
            productIdCtr.setProp(
              "optionItems",
              woDetail.items?.map((x) => ({
                value: x.product?.id,
                label: createProductOptionLabel(x.product as IProductProperties),
              }))
            );
          }
        });
      }
    }
  }, [
    getControl,
    dualLangInspectionNoteDetail,
    setOptionItemsForProduct,
    setOptionItemsForProductFromWorkOrderPo,
  ]);

  const disableSomeFieldIfStandardIsEmpty = useCallback(
    (data: IUpdateDualLangInspectionNoteItem) => {
      if (!data) return;
      // disable some fields if standard is empty
      console.log("data", data);
      getArrayItemControl("items", 0, "heightInspect").setProp(
        "disabled",
        !data.heightStandard
      );
      getArrayItemControl("items", 0, "heightResult").setProp(
        "disabled",
        !data.heightStandard
      );

      getArrayItemControl("items", 0, "lengthInspect").setProp(
        "disabled",
        !data.lengthStandard
      );
      getArrayItemControl("items", 0, "lengthResult").setProp(
        "disabled",
        !data.lengthStandard
      );

      getArrayItemControl("items", 0, "thicknessInspect").setProp(
        "disabled",
        !data.thicknessStandard
      );
      getArrayItemControl("items", 0, "thicknessResult").setProp(
        "disabled",
        !data.thicknessStandard
      );

      getArrayItemControl("items", 0, "weightInspect").setProp(
        "disabled",
        !data.weightStandard
      );
      getArrayItemControl("items", 0, "weightResult").setProp(
        "disabled",
        !data.weightStandard
      );

      getArrayItemControl("items", 0, "widthInspect").setProp(
        "disabled",
        !data.widthStandard
      );
      getArrayItemControl("items", 0, "widthResult").setProp(
        "disabled",
        !data.widthStandard
      );

      getArrayItemControl("items", 0, "approvalInspect").setProp(
        "disabled",
        !data.approvalStandard
      );
      getArrayItemControl("items", 0, "approvalResult").setProp(
        "disabled",
        !data.approvalStandard
      );

      getArrayItemControl("items", 0, "printColorInspect").setProp(
        "disabled",
        !data.printColorStandard
      );
      getArrayItemControl("items", 0, "printColorResult").setProp(
        "disabled",
        !data.printColorStandard
      );
    },
    [getArrayItemControl]
  );

  useEffect(() => {
    if (dualLangInspectionNoteDetail) {
      const newItem = dualLangInspectionNoteDetail?.items?.map((x) => ({
        id: x.id,
        appearanceResult: x.appearanceResult || InspectionConclusionEnum.None,
        approval: "",
        approvalResult: x.appearanceResult || InspectionConclusionEnum.None,
        approvalStandard:
          (x.product && createApprovalStandard(x.product)) || "",
        height: "",
        heightResult: x.heightResult || InspectionConclusionEnum.None,
        heightStandard: (x.product && createHeightStandard(x.product)) || "",
        length: "",
        lengthResult: x.lengthResult || InspectionConclusionEnum.None,
        lengthStandard: (x.product && createLengthStandard(x.product)) || "",
        otherStandard: "",
        otherStandardResult:
          x.otherStandardResult || InspectionConclusionEnum.None,
        otherStandardStandard: "",
        paperType: "",
        paperTypeInspecctionStandard:
          (x.product && createPaperTypeStandard(x.product)) || "",
        paperTypeInspect: x.paperTypeInspect || 0,
        paperTypeResult: x.paperTypeResult || InspectionConclusionEnum.None,
        printColor: "",
        printColorResult: x.printColorResult || InspectionConclusionEnum.None,
        printColorStandard:
          (x.product && createPrintColorStandard(x.product)) || "",
        thickness: "",
        thicknessResult: x.thicknessResult || InspectionConclusionEnum.None,
        thicknessStandard:
          (x.product && createThicknessStandard(x.product)) || "",
        weight: "",
        weightResult: x.weightResult || InspectionConclusionEnum.None,
        weightStandard: (x.product && createWeightStandard(x.product)) || "",
        width: "",
        widthResult: x.widthResult || InspectionConclusionEnum.None,
        widthStandard: (x.product && createWidthStandard(x.product)) || "",
        appearanceInspectionStandard: x.appearanceInspectionStandard || "",
        appearanceInspect: x.appearanceInspect || "",
        approvalInspect: x.approvalInspect || "",
        conclusion: x.conclusion || "",
        heightInspect: x.heightInspect || 0,
        lengthInspect: x.lengthInspect || 0,
        otherStandardInspect: x.otherStandardInspect || "",
        thicknessInspect: x.thicknessInspect || 0,
        printColorInspect: x.printColorInspect || "",
        weightInspect: x.weightInspect || 0,
        widthInspect: x.widthInspect || 0,
        productId: x.productId,
        apperance: "",
        otherStandardInspectionStandard:
          (x.product?.dimensionWeight &&
            getOthersStandard(x.product?.dimensionWeight)) ||
          "",
      }))?.[0];

      setDefaults({
        id: dualLangInspectionNoteDetail.id,
        expireDateText: dualLangInspectionNoteDetail?.expireDateText || "",
        inspectionDate: dualLangInspectionNoteDetail?.inspectionDate || "",
        number: dualLangInspectionNoteDetail?.number || "",
        workOrderId: dualLangInspectionNoteDetail?.workOrderId || "",
        workOrderPOId: dualLangInspectionNoteDetail?.workOrderPOId || "",
        conclusion:
          dualLangInspectionNoteDetail.conclusion || conclusionDefautText,
        customerId: dualLangInspectionNoteDetail.customerId || "",
        deliveredQuantity: dualLangInspectionNoteDetail.deliveredQuantity || 0,
        inspectionSamples: dualLangInspectionNoteDetail.inspectionSamples || "",
        inspector: dualLangInspectionNoteDetail.inspector || "",
        note: dualLangInspectionNoteDetail.note || "",
        packagesQuantity: dualLangInspectionNoteDetail.packagesQuantity || "",
        purchaseOrderId: dualLangInspectionNoteDetail.purchaseOrderId || "",
        quantityPerPackage:
          dualLangInspectionNoteDetail.quantityPerPackage || 0,
        storageTemperature:
          dualLangInspectionNoteDetail.storageTemperature || "",
        items: newItem
          ? [
              {
                ...newItem,
                approvalInspect: !newItem.approvalStandard
                  ? ""
                  : approvalInspectDefaultText,
                printColorInspect: !newItem.printColorStandard
                  ? ""
                  : PrintColorInspect.Standard,
              },
            ]
          : [],
      });
      if (newItem) {
        disableSomeFieldIfStandardIsEmpty(newItem);
      }

      setOptionItemsSchemaForProduct();
    }
  }, [
    getControl,
    dualLangInspectionNoteDetail,
    setDefaults,
    setOptionItemsSchemaForProduct,
    disableSomeFieldIfStandardIsEmpty,
    getControlArray,
  ]);

  useEffect(() => {
    const woPoCtr = getControl("workOrderPOId");
    woPoCtr.setProp("optionItems", [
      {
        value: dualLangInspectionNoteDetail?.workOrderPOId,
        label: `${
          dualLangInspectionNoteDetail?.workOrderPO?.woNumber ||
          dualLangInspectionNoteDetail?.workOrder?.woNumber ||
          ""
        }`,
      },
    ]);

    const customerCtr = getControl("customerId");
    customerCtr.setProp("optionItems", [
      {
        value: dualLangInspectionNoteDetail?.customerId,
        label: `${dualLangInspectionNoteDetail?.customer?.dealingName || ""}`,
      },
    ]);

    const orderCtr = getControl("purchaseOrderId");
    orderCtr.setProp("optionItems", [
      {
        value: dualLangInspectionNoteDetail?.purchaseOrderId,
        label: `${dualLangInspectionNoteDetail?.purchaseOrder?.poNumber || ""}`,
      },
    ]);
  }, [
    getControl,
    dualLangInspectionNoteDetail?.customer?.dealingName,
    dualLangInspectionNoteDetail?.customerId,
    dualLangInspectionNoteDetail?.workOrderPO?.woNumber,
    dualLangInspectionNoteDetail?.workOrderPOId,
    dualLangInspectionNoteDetail?.workOrder?.woNumber,
    dualLangInspectionNoteDetail?.purchaseOrderId,
    dualLangInspectionNoteDetail?.purchaseOrder?.poNumber,
  ]);

  const saveHandler = useCallback(
    async (data: IUpdateDualLangInspectionNote) => {
      try {
        if (!dualLangInspectionNoteDetail?.id) return;
        await dualLangInspectionNoteBusiness.update(
          dualLangInspectionNoteDetail?.id,
          data
        );
        await getDualLangInspectionNoteDetail(dualLangInspectionNoteDetail?.id);
        setProp("openEdit", false);
      } catch (ex) {
        console.error(ex);
      }
    },
    [getDualLangInspectionNoteDetail, dualLangInspectionNoteDetail?.id, setProp]
  );

  const dualLangDualLangInspectionNoteItems = useMemo(() => {
    return getControlArray("items");
  }, [getControlArray]);

  const valueChangeHandler = useCallback(
    async (control: IControl) => {
      if (control.name === "customerId") {
        getControl("workOrderPOId").setProp("value", "");
        cleanItems();
        setOptionItemsForProductIsEmty();
        getControl("purchaseOrderId").setProp("value", "");
        itemControl.addChildren<IUpdateDualLangInspectionNoteItem>([
          {
            ...inspectionItemDefaultValues,
          },
        ]);
      }
      if (control.name === "workOrderPOId") {
        try {
          setRootStoreProp("appFreeze", true);
          if (control.value) {
            cleanItems();
            setOptionItemsForProductIsEmty();
            const wo = await workOrderPoBusiness.getById(control.value);
            setOptionItemsForProductFromWorkOrderPo(wo);

            const woProductId = wo.purchaseOrderItem?.productId;
            if (woProductId) {
              const product = await productBussiness.getById(woProductId);
              const otherStandardInspectionStandard =
                (product.dimensionWeight &&
                  getOthersStandard(product.dimensionWeight)) ||
                "";
              const otherStandardInspect = otherStandardInspectionStandard
                ? otherInspectDefaultText
                : "";
              const newData: IUpdateDualLangInspectionNoteItem = {
                ...inspectionItemDefaultValues,
                approvalStandard:
                  (product && createApprovalStandard(product)) || "",
                heightStandard:
                  (product && createHeightStandard(product)) || "",
                lengthStandard:
                  (product && createLengthStandard(product)) || "",
                paperTypeInspecctionStandard:
                  (product && createPaperTypeStandard(product)) || "",
                printColorStandard:
                  (product && createPrintColorStandard(product)) || "",
                thicknessStandard:
                  (product && createThicknessStandard(product)) || "",
                weightStandard:
                  (product && createWeightStandard(product)) || "",
                widthStandard: (product && createWidthStandard(product)) || "",
                productId: product.id || "",
                otherStandardInspectionStandard:
                  (product.dimensionWeight &&
                    getOthersStandard(product.dimensionWeight)) ||
                  "",
                otherStandardInspect: otherStandardInspect,
                appearanceInspectionStandard:
                  product.dimensionWeight?.appearanceText ?? "",
              };

              itemControl.addChildren<IUpdateDualLangInspectionNoteItem>([
                {
                  ...newData,
                  approvalInspect: !newData.approvalStandard
                    ? ""
                    : approvalInspectDefaultText,
                  printColorInspect: !newData.printColorStandard
                    ? ""
                    : PrintColorInspect.Standard,
                  appearanceInspect: newData.appearanceInspectionStandard
                    ? apperanceInspectDefaultText
                    : "",
                },
              ]);

              // disable some fields if standard is empty

              disableSomeFieldIfStandardIsEmpty(newData);
            }
          }
        } catch (ex) {
        } finally {
          setRootStoreProp("appFreeze", false);
        }
      }
    },
    [
      cleanItems,
      disableSomeFieldIfStandardIsEmpty,
      getControl,
      itemControl,
      setOptionItemsForProductFromWorkOrderPo,
      setOptionItemsForProductIsEmty,
      setRootStoreProp,
    ]
  );

  const onItemsValueChanged = useCallback(
    (control: IControl, rowIndex: number) => {},
    []
  );

  return (
    <DialogForm
      open={openEdit}
      fullWidth
      maxWidth={"xl"}
      onClose={() => setProp("openEdit", false)}
      onCancelBtnClicked={() => setProp("openEdit", false)}
      onSaveBtnClick={async () => await submitHandler(saveHandler)}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>Sửa phiếu kiểm nghiệm</DialogTitle>
      <DialogComplexContent>
        <DialogComplexFormContainer>
          <DynamicForm
            controls={controls}
            onValueChanged={valueChangeHandler}
          ></DynamicForm>
        </DialogComplexFormContainer>
        <DialogComplexFormArrayContainer label={itemControl.label}>
          {dualLangDualLangInspectionNoteItems &&
            dualLangDualLangInspectionNoteItems.length > 0 && (
              <DynamicFormArray
                containerProps={{
                  sx: {
                    marginTop: "10px",
                    marginBottom: "10px",
                  },
                }}
                controlSpacing={2}
                children={dualLangDualLangInspectionNoteItems}
                onValueChanged={onItemsValueChanged}
              ></DynamicFormArray>
            )}
        </DialogComplexFormArrayContainer>
      </DialogComplexContent>
    </DialogForm>
  );
});
